// Small formatters used across pages.

import { runStatusPillClass } from './runStatus'

/**
 * Format an absolute timestamp as a relative phrase.
 *
 * Past times: "23s ago" / "5m ago" / "2h 14m ago" / "3d 4h ago" / "in 5d"
 * Future times: "in 47s" / "in 12m" / "in 5h 53m" / "in 18h 30m"
 *
 * Uses two-unit precision once the gap is > 1 hour, so users see "5h 53m"
 * instead of "6h" (rounded) or "21203s" (raw seconds — the previous bug).
 */
export function formatRelative(iso: string | null): string {
  if (!iso) return '—'
  // FastAPI emits naive UTC (datetime.utcnow().isoformat() → no trailing 'Z').
  // JS Date parses TZ-less ISO as LOCAL time, which puts every backend
  // timestamp ~4-8 hours in the wrong direction depending on the operator's
  // zone. Treat any TZ-less timestamp as UTC so relative phrases are correct.
  const hasTz = iso.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(iso)
  const d = new Date(hasTz ? iso : iso + 'Z')
  if (isNaN(d.getTime())) return iso

  // diff > 0 = in the past, diff < 0 = in the future
  const diff = Math.round((Date.now() - d.getTime()) / 1000)
  const future = diff < 0
  const abs = Math.abs(diff)

  const phrase = humanize(abs)
  return future ? `in ${phrase}` : `${phrase} ago`
}

/**
 * Render a positive duration in seconds as a short human phrase.
 * Single-unit under 1 hour; two-unit precision above (hours+min, days+hours).
 */
export function humanize(seconds: number): string {
  if (seconds < 1) return '0s'
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) {
    const m = Math.round(seconds / 60)
    return `${m}m`
  }
  if (seconds < 86400) {
    // e.g. 21203s = 5h 53m
    const h = Math.floor(seconds / 3600)
    const m = Math.round((seconds % 3600) / 60)
    return m > 0 ? `${h}h ${m}m` : `${h}h`
  }
  // > 1 day
  if (seconds < 86400 * 30) {
    const d = Math.floor(seconds / 86400)
    const h = Math.round((seconds % 86400) / 3600)
    return h > 0 ? `${d}d ${h}h` : `${d}d`
  }
  // > 30 days — coarsen further
  const months = Math.round(seconds / (86400 * 30))
  return `${months}mo`
}

export type DateRangePreset = '24h' | '7d' | '30d' | 'custom' | 'all'

/**
 * Convert a date-range preset to {from?, to?} ISO datetime strings (UTC).
 * `all` and `custom` both return an empty object — `custom` defers to caller-
 * supplied bounds, `all` means "no filter".
 */
export function presetToRange(preset: DateRangePreset): { from?: string; to?: string } {
  if (preset === 'all' || preset === 'custom') return {}
  const now = new Date()
  const days = ({ '24h': 1, '7d': 7, '30d': 30 } as const)[preset]
  const since = new Date(now.getTime() - days * 24 * 3600 * 1000)
  return { from: since.toISOString(), to: now.toISOString() }
}

/** Format a USD amount with $ + thousands separators + 2 decimals. */
export function fmtMoney(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—'
  return v.toLocaleString('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
  })
}

/** Format an amount in a named ISO currency (CAD, GBP, EUR...). A missing
 *  code or USD is exactly fmtMoney, so a US figure never changes shape. */
export function fmtMoneyIn(v: number | null | undefined, currency: string | null | undefined): string {
  if (!currency || currency === 'USD') return fmtMoney(v)
  if (v === null || v === undefined || Number.isNaN(v)) return '—'
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(v)
  } catch {
    // A malformed code: keep the number and say which currency it is.
    return `${fmtMoney(v).replace('$', '')} ${currency}`
  }
}

/** Format a 0..1 fraction (or 0..100 if scale='percent') as a % with 1 decimal. */
export function fmtPercent(v: number | null | undefined, scale: 'fraction' | 'percent' = 'fraction'): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—'
  const pct = scale === 'fraction' ? v * 100 : v
  return `${pct.toFixed(1)}%`
}

const POINTS_FMT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
/** Format a change in percentage points (e.g. ACoS 18% -> 20% is "2.0pp"),
 *  1 decimal. The sign is the caller's: pass Math.abs for a size-only label. */
export function fmtPoints(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—'
  return `${POINTS_FMT.format(v)}pp`
}

/**
 * Format an ACoS value, sentinel-aware. The BQ PPC views emit 99.99 when
 * a row has spend > $0 and sales = $0 (mathematically infinite ACoS); the
 * sentinel keeps those rows visible to "ACoS > X" rule filters instead of
 * dropping them as NULL. Without sentinel detection, fmtPercent renders
 * 99.99 as "9999.0%" which looks like a broken metric.
 *
 * See memory: reference_adbetter_acos_sentinel.md.
 */
export const ACOS_SENTINEL = 99.99
export function isAcosSentinel(v: number | null | undefined): boolean {
  return v != null && v >= ACOS_SENTINEL - 0.5
}
export function fmtAcos(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—'
  if (isAcosSentinel(v)) return '∞%'
  return `${(v * 100).toFixed(1)}%`
}

const MULTIPLE_FMT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Format a multiple such as ROAS: 2 decimals + "×" (3.456 -> "3.46×"). */
export function fmtMultiple(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—'
  return `${MULTIPLE_FMT.format(v)}×`
}

/** Format an integer with thousands separators. */
export function fmtInt(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—'
  return Math.round(v).toLocaleString('en-US')
}

/** CPC = spend / clicks. Null when clicks is 0/missing or spend is missing —
 *  renders as "—" instead of 0 / Infinity / NaN. Shared by every PPC entity
 *  table that computes CPC client-side (Targets/Keywords/SearchTerms). */
export function calcCpc(spend: number | null, clicks: number | null): number | null {
  return clicks && clicks > 0 && spend != null ? spend / clicks : null
}

/** CTR = clicks / impressions. Null when impressions is 0/missing or clicks
 *  is missing. Shared by the same PPC entity tables as `calcCpc`. */
export function calcCtr(clicks: number | null, impressions: number | null): number | null {
  return impressions && impressions > 0 && clicks != null ? clicks / impressions : null
}

// Status pill classes — design-system semantic tokens (theme-aware).
//
// Backed by the shared run-status vocabulary (lib/runStatus.ts) so every one
// of the 18 statuses a run can carry gets a real color instead of falling to
// the gray default. Pass the run's test flag (isTestRun(r.dry_run)) as
// `dryRun`: a test run's 'success' is the info tone, not the green of a real
// change. Per-record CHANGE statuses have their own vocabulary
// (lib/changeStatus.ts) and no longer come through here.
export function statusColor(status: string | null, dryRun = false): string {
  return runStatusPillClass(status, dryRun)
}

// ── Compact large numbers (PR 9a, L10) ───────────────────────────────────────
// A table cell showing 11,141,642 next to 100,317 ran the two together. From a
// million up the cell shows 11.1M and carries the exact figure in its tooltip.

/** True from one million up (in either direction). */
export function isCompactRange(v: number | null | undefined): v is number {
  return typeof v === 'number' && Number.isFinite(v) && Math.abs(v) >= 1e6
}

/**
 * `11141642` -> `11.1M`, `1.2e9` -> `1.2B`; money gets a `$`. One decimal is
 * always kept (`11.0M`) so widths stay stable. Below a million this is exactly
 * `fmtInt` / `fmtMoney`.
 */
export function fmtCompact(v: number | null | undefined, kind: 'int' | 'money'): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—'
  if (!isCompactRange(v)) return kind === 'money' ? fmtMoney(v) : fmtInt(v)
  const abs = Math.abs(v)
  // 999,999,999 rounds to "1000.0M" at one decimal -- promote it to 1.0B.
  const [div, suffix] = abs >= 1e9 || Number((abs / 1e6).toFixed(1)) >= 1000
    ? [1e9, 'B'] as const : [1e6, 'M'] as const
  const body = `${(abs / div).toFixed(1)}${suffix}`
  return `${v < 0 ? '-' : ''}${kind === 'money' ? '$' : ''}${body}`
}
